// Step-up (AUTH-02, SECURITY §2.1): a sensitive call that answers 401 `auth.stepup_required` opens
// this dialog; once the user confirms with a method the server accepts, the original call is retried
// exactly once. Since 2026-11-05 (owner: the message showed with no popup on screens that had not
// wrapped their call) this happens for EVERY call: the provider installs the handler in the API
// client (`setStepUpHandler`), so no screen can forget it. `useStepUp()` stays for older callers and
// is now a plain pass-through:
//
//   const stepUp = useStepUp();
//   await stepUp(() => api.changeMemberRole(orgId, memberId, roleId));
//
// Methods (identity handlers-mfa.ts StepUpBody): an email code (otp/start purpose=stepup), the
// authenticator app (totp), a passkey (passkeys/options purpose=stepup) and a recovery code. The
// list is narrowed to what this account has (GET /me mfa) and, when present, to `details.methods`.
// Owner, 2026-11-04 (D-126): someone with an authenticator or passkey sees only those (and a recovery
// code): the server never takes an emailed or texted code from them. Everyone else sees "Email me a
// code", and "Text me a code" when their account has a verified mobile number and the shop they are
// in has SMS credits (1 credit per code, paid by that shop).
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { browserSupportsWebAuthn, startAuthentication } from "@simplewebauthn/browser";
import { normalizeDigits } from "@ady/format";
import { t } from "@ady/i18n";
import { Banner, cx, Dialog, Field, Icon, Input, type IconName } from "@ady/ui";
import { ApiError, StepUpCancelled, api, setStepUpHandler } from "../api/client";
import { errorMessage } from "../api/errors";
import { keys } from "../api/queries";
import { smsApi } from "../services/smsApi";
import type { MeResponse, StepUpMethod } from "../api/types";
import { TID } from "../testids";

/** Thrown to the caller when the user closes the step-up dialog (defined in the API client). */
export { StepUpCancelled };
export const isStepUpCancelled = (err: unknown): err is StepUpCancelled =>
  err instanceof StepUpCancelled;

export const isStepUpRequired = (err: unknown): err is ApiError =>
  err instanceof ApiError && err.status === 401 && err.code === "auth.stepup_required";

type Run = <T>(fn: () => Promise<T>) => Promise<T>;
const StepUpContext = createContext<Run | null>(null);

export function useStepUp(): Run {
  const run = useContext(StepUpContext);
  if (!run) throw new Error("useStepUp must be used inside <StepUpProvider>");
  return run;
}

interface Waiter {
  resolve: () => void;
  reject: (err: unknown) => void;
}

const ORDER: StepUpMethod[] = ["totp", "passkey", "otp", "recovery_code"];

/** A choice in the dialog: a server method, or "sms" (an otp code sent by text). */
export type StepUpChoice = StepUpMethod | "sms";

/** The methods this account can use, in preference order (see the header for the rules). */
export function availableMethods(
  me: MeResponse | undefined,
  serverList?: unknown,
  smsReady = false,
): StepUpChoice[] {
  const user = me?.user;
  if (!user) return [];
  const hasFactor = user.mfa.totp || user.mfa.passkeys > 0;
  const has: Record<StepUpMethod, boolean> = {
    totp: user.mfa.totp,
    passkey: user.mfa.passkeys > 0 && browserSupportsWebAuthn(),
    // Codes by email or text only for accounts without a real second factor (server rule #1).
    otp: Boolean(user.email) && !hasFactor,
    recovery_code: hasFactor,
  };
  const allowed = Array.isArray(serverList)
    ? new Set(serverList.filter((m): m is string => typeof m === "string"))
    : null;
  const ok = (m: StepUpMethod) => !allowed || allowed.has(m);
  const list: StepUpChoice[] = ORDER.filter((m) => has[m] && ok(m));
  if (!hasFactor && smsReady && user.phone && ok("otp")) {
    const at = list.indexOf("otp");
    list.splice(at < 0 ? list.length : at + 1, 0, "sms");
  }
  return list;
}

/** The shop in the address bar (`/<org-slug>/...`), which pays for SMS codes. */
function currentOrg(me: MeResponse | undefined) {
  const slug = location.pathname.split("/")[1] ?? "";
  return me?.memberships.find((m) => m.org.slug === slug)?.org ?? null;
}

export function StepUpProvider({ children }: { children: ReactNode }) {
  const waiters = useRef<Waiter[]>([]);
  const [request, setRequest] = useState<{ methods?: unknown } | null>(null);

  const settle = useCallback((ok: boolean) => {
    const list = waiters.current;
    waiters.current = [];
    setRequest(null);
    for (const w of list) {
      if (ok) w.resolve();
      else w.reject(new StepUpCancelled());
    }
  }, []);

  // Every API call asks through this (api/client.ts). Concurrent sensitive calls share one dialog.
  useEffect(() => {
    setStepUpHandler(
      (methods) =>
        new Promise<void>((resolve, reject) => {
          waiters.current.push({ resolve, reject });
          setRequest((r) => r ?? { methods });
        }),
    );
    return () => setStepUpHandler(null);
  }, []);

  // The client already asked and retried once; a second refusal reaches the caller as it is.
  const run = useCallback<Run>((fn) => fn(), []);

  return (
    <StepUpContext.Provider value={run}>
      {children}
      <StepUpDialog
        open={request !== null}
        serverMethods={request?.methods}
        onDone={() => settle(true)}
        onCancel={() => settle(false)}
      />
    </StepUpContext.Provider>
  );
}

const METHOD_ICON: Record<StepUpChoice, IconName> = {
  sms: "phone",
  otp: "inbox",
  totp: "key",
  passkey: "fingerprint",
  recovery_code: "shield",
};

function StepUpDialog({
  open,
  serverMethods,
  onDone,
  onCancel,
}: {
  open: boolean;
  serverMethods: unknown;
  onDone: () => void;
  onCancel: () => void;
}) {
  // Only while open: the provider also wraps the signed-out pages.
  const me = useQuery({
    queryKey: keys.me,
    queryFn: ({ signal }) => api.me(signal),
    enabled: open,
  });
  const qc = useQueryClient();
  const user = me.data?.user;
  const org = currentOrg(me.data);
  const smsWanted = Boolean(
    open && user?.phone && org && !user.mfa.totp && !(user.mfa.passkeys > 0),
  );
  // The shop's SMS credits (Notifications, SMS); unreadable (no permission) = no SMS option.
  const sms = useQuery({
    queryKey: ["org", org?.id ?? "", "sms", "account"],
    queryFn: () => smsApi.account(org!.id),
    enabled: smsWanted,
    retry: false,
  });
  const methods = availableMethods(me.data, serverMethods, (sms.data?.balance ?? 0) > 0);
  const [method, setMethod] = useState<StepUpChoice | null>(null);
  const [challenge, setChallenge] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [code, setCode] = useState("");
  const [fieldError, setFieldError] = useState<string>();
  const [formError, setFormError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const email = me.data?.user.email ?? "";
  const phone = me.data?.user.phone ?? "";

  useEffect(() => {
    if (open) return;
    setMethod(null);
    setChallenge(null);
    setCode("");
    setFieldError(undefined);
    setFormError(undefined);
  }, [open]);

  // A single method needs no choice; a code is not sent until the user asks for it.
  useEffect(() => {
    const only = methods.length === 1 ? methods[0] : undefined;
    if (open && method === null && only && only !== "otp" && only !== "sms") setMethod(only);
  }, [open, method, methods]);

  const choose = async (m: StepUpChoice) => {
    setMethod(m);
    setCode("");
    setFieldError(undefined);
    setFormError(undefined);
    if (m !== "otp" && m !== "sms") return;
    setChallenge(null);
    setSending(true);
    try {
      const res =
        m === "sms"
          ? await api.otpStart({
              channel: "phone",
              value: phone,
              purpose: "stepup",
              org_id: org!.id,
            })
          : await api.otpStart({ channel: "email", value: email, purpose: "stepup" });
      setChallenge(res.challenge_id);
      if (m === "sms") void qc.invalidateQueries({ queryKey: ["org", org!.id, "sms", "account"] });
    } catch (err) {
      setFormError(errorMessage(err));
    } finally {
      setSending(false);
    }
  };

  const finish = async (body: Parameters<typeof api.stepUp>[0]) => {
    await api.stepUp(body);
    await qc.invalidateQueries({ queryKey: keys.me });
    onDone();
  };

  const confirm = async (e?: FormEvent) => {
    e?.preventDefault();
    setFieldError(undefined);
    setFormError(undefined);
    if (!method) return setFormError(t("stepUp.chooseFirst"));
    const value = code.trim();
    if ((method === "otp" || method === "sms" || method === "totp") && !/^\d{6}$/.test(value))
      return setFieldError(t("stepUp.codeInvalid"));
    if (method === "recovery_code" && value.length < 10)
      return setFieldError(t("stepUp.recoveryInvalid"));
    setBusy(true);
    try {
      if (method === "otp" || method === "sms") {
        if (!challenge) return setFormError(t("stepUp.chooseFirst"));
        await finish({ method: "otp", challenge_id: challenge, code: value });
      } else if (method === "totp") {
        await finish({ method: "totp", code: value });
      } else if (method === "recovery_code") {
        await finish({ method: "recovery_code", code: value });
      } else {
        const opts = await api.passkeyOptions("stepup");
        const response = await startAuthentication({ optionsJSON: opts.options as never });
        await finish({ method: "passkey", challenge_id: opts.challenge_id, response });
      }
    } catch (err) {
      const msg =
        err instanceof Error && err.name === "NotAllowedError"
          ? t("auth.passkeyCancelled")
          : errorMessage(err);
      if (method === "passkey") setFormError(msg);
      else setFieldError(msg);
    } finally {
      setBusy(false);
    }
  };

  // The email-code field appears only once the code is on its way (no confirm before a challenge).
  const needsCode =
    ((method === "otp" || method === "sms") && challenge !== null) ||
    method === "totp" ||
    method === "recovery_code";
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      title={t("stepUp.title")}
      description={t("stepUp.lead")}
      size="sm"
      data-testid={TID.stepUpDialog}
      confirm={
        methods.length
          ? {
              label: t("stepUp.confirm"),
              loading: busy || sending,
              loadingLabel: t("stepUp.confirming"),
              onClick: () => void confirm(),
              "data-testid": TID.stepUpConfirm,
            }
          : undefined
      }
    >
      <div className="flex flex-col gap-4 pt-4">
        {formError ? <Banner kind="error" title={formError} /> : null}
        {methods.length === 0 ? (
          <Banner kind="error" title={t("stepUp.noMethods")} />
        ) : (
          <fieldset className="flex flex-col gap-2">
            <legend className="pb-2 text-sm font-medium">{t("stepUp.methodsLabel")}</legend>
            {methods.map((m) => (
              <button
                key={m}
                type="button"
                aria-pressed={method === m}
                data-testid={TID.stepUpMethod(m)}
                onClick={() => void choose(m)}
                className={cx(
                  "flex min-h-12 items-center gap-3 rounded-sm border px-3 text-start text-md",
                  method === m
                    ? "border-action-border bg-selected font-medium"
                    : "border-subtle hover:bg-hover",
                )}
              >
                <Icon name={METHOD_ICON[m]} className="flex-none text-fg-secondary" />
                <span className="flex-1">{t(`stepUp.methods.${m}`)}</span>
                {method === m ? <Icon name="check" className="text-action-fg" /> : null}
              </button>
            ))}
          </fieldset>
        )}
        {method === "otp" || method === "sms" ? (
          <p className="text-sm text-fg-secondary" role="status">
            {sending
              ? t("stepUp.otpSending")
              : challenge
                ? method === "sms"
                  ? t("stepUp.smsSent", { phone, shop: org?.name ?? "" })
                  : t("stepUp.otpSent", { email })
                : null}
          </p>
        ) : null}
        {method === "passkey" ? (
          <p className="text-sm text-fg-secondary">{t("stepUp.passkeyHint")}</p>
        ) : null}
        {needsCode ? (
          <form onSubmit={confirm} noValidate>
            <Field
              label={method === "recovery_code" ? t("stepUp.recoveryLabel") : t("stepUp.codeLabel")}
              error={fieldError}
              required
            >
              <Input
                value={code}
                inputMode={method === "recovery_code" ? "text" : "numeric"}
                autoComplete="one-time-code"
                autoCapitalize="none"
                spellCheck={false}
                maxLength={method === "recovery_code" ? 16 : 6}
                ledger
                onChange={(e) =>
                  setCode(
                    method === "recovery_code"
                      ? e.target.value
                      : normalizeDigits(e.target.value).replace(/\D/g, "").slice(0, 6),
                  )
                }
                data-testid={TID.stepUpCodeInput}
                autoFocus
              />
            </Field>
          </form>
        ) : null}
      </div>
    </Dialog>
  );
}

/** Error copy for a failed sensitive call: null when the user closed the step-up dialog. */
export function stepUpErrorMessage(err: unknown): string | null {
  return isStepUpCancelled(err) ? null : errorMessage(err);
}
